import { useCallback, useRef, useState } from "react";
import { toast } from "@codelittinc/carbon-design-system";
import type {
  QuizAnswerRequest,
  QuizAnswerResponse,
  QuizAttempt,
  QuizResult,
  QuizStateResponse,
  QuizSubmitResponse,
  QuizSummary,
  TrainingItem,
} from "@carbon-os/server/contract";
import { trackTraining } from "./analytics";
import { celebrate, failedOf, fetchNow, TRACK_EXPIRED, type Failed, type TrainingControls } from "./training";

/** A quiz can be taken any time its track is open, however much of its video was watched. */
export function canTakeQuiz(item: TrainingItem): boolean {
  return !item.locked;
}

/** What a row says about its quiz. `none` is today's checkbox row. */
export type QuizRowState =
  | { kind: "none" }
  | { kind: "not_started"; questionCount: number; passPercent: number }
  | { kind: "in_progress"; answered: number; total: number }
  /** `bestPercent` is null for a material marked done without a passing score, ticked before its quiz existed. */
  | { kind: "passed"; bestPercent: number | null }
  | { kind: "failed"; bestPercent: number; passPercent: number };

/** Done wins, then an open attempt, then a submitted one; Gatekeeper never says "failed" itself. */
export function quizRowState(quiz: QuizSummary | null, completedAt: string | null): QuizRowState {
  if (!quiz) return { kind: "none" };
  if (completedAt) {
    const best = quiz.bestPercent;
    return { kind: "passed", bestPercent: best !== null && best >= quiz.passPercent ? best : null };
  }
  if (quiz.inProgress) return { kind: "in_progress", ...quiz.inProgress };
  if (quiz.attempts > 0) return { kind: "failed", bestPercent: quiz.bestPercent ?? 0, passPercent: quiz.passPercent };
  return { kind: "not_started", questionCount: quiz.questionCount, passPercent: quiz.passPercent };
}

export const QUIZ_GONE = "This quiz is no longer available";

type Call<T> = { ok: true; body: T } | Failed;

/** One quiz request to carbon-os. `status` is 0 when it never arrived. */
async function quizRequest<T>(
  fetchImpl: typeof fetch,
  path: string,
  method: "GET" | "POST" | "PUT",
  json?: QuizAnswerRequest,
): Promise<Call<T>> {
  let res: Response;
  try {
    res = await fetchImpl(path, {
      method,
      ...(method === "GET" ? { cache: "no-store" as const } : {}),
      credentials: "same-origin",
      headers: { Accept: "application/json", ...(json ? { "Content-Type": "application/json" } : {}) },
      ...(json ? { body: JSON.stringify(json) } : {}),
    });
  } catch {
    return { ok: false, status: 0 };
  }
  if (res.status !== 200) return failedOf(res);
  const body = (await res.json().catch(() => null)) as T | null;
  if (!body) return { ok: false, status: res.status };
  return { ok: true, body };
}

const attemptPath = (materialId: string) => `/api/training/quizzes/${encodeURIComponent(materialId)}/attempt`;

export function loadQuizState(fetchImpl: typeof fetch, materialId: string): Promise<Call<QuizStateResponse>> {
  return quizRequest(fetchImpl, attemptPath(materialId), "GET");
}

/** Resumes the open attempt or starts one; Gatekeeper makes twice the same attempt. */
export function startQuiz(fetchImpl: typeof fetch, materialId: string): Promise<Call<QuizAttempt>> {
  return quizRequest(fetchImpl, attemptPath(materialId), "POST");
}

export function saveAnswer(
  fetchImpl: typeof fetch,
  materialId: string,
  questionId: string,
  choiceId: string,
): Promise<Call<QuizAnswerResponse>> {
  return quizRequest(fetchImpl, `${attemptPath(materialId)}/answers/${encodeURIComponent(questionId)}`, "PUT", {
    choiceId,
  });
}

export function submitQuiz(fetchImpl: typeof fetch, materialId: string): Promise<Call<QuizSubmitResponse>> {
  return quizRequest(fetchImpl, `${attemptPath(materialId)}/submit`, "POST");
}

export type SaveState = "saving" | "saved" | "failed";

type Answering = { attempt: QuizAttempt; index: number; saves: Record<string, SaveState> };

export type QuizView =
  /** `gone` when it closed because the quiz no longer exists. */
  | { kind: "closed"; gone?: true }
  | { kind: "loading" }
  | { kind: "unavailable"; retrying: boolean }
  | ({ kind: "answering"; notice?: "incomplete" | "submit_failed" } & Answering)
  | ({ kind: "submitting" } & Answering)
  | { kind: "result"; result: QuizResult };

/** Every question has an answer Gatekeeper confirmed: the only time Submit is offered. */
export function allSaved({ attempt, saves }: Answering): boolean {
  return attempt.questions.every((question) => saves[question.id] === "saved");
}

/** One question's saves: at most one request out, and the latest choice waiting behind it. */
interface AnswerQueue {
  sending: boolean;
  desired: string;
}

/** The quiz that is open. Async results for any other are dropped. */
interface Session {
  item: TrainingItem;
  trackId: string;
  /** Submitted attempts, counting this visit's. */
  attempts: number;
  /** An attempt was already open when the quiz was opened. */
  resumable: boolean;
  /** Set once an attempt has been reloaded; a second failure shows the unavailable panel. */
  reloaded: boolean;
  /** An answer was saved or an attempt submitted since the list last re-read. */
  dirty: boolean;
}

function answering(attempt: QuizAttempt, notice?: "incomplete"): QuizView {
  const resume = attempt.questions.findIndex((question) => question.id === attempt.resumeAt);
  // Nothing left to resume because every question has an answer: open where Submit is.
  const index =
    resume >= 0 ? resume : attempt.questions.every((question) => question.answer !== null) ? attempt.questions.length - 1 : 0;
  const saves: Record<string, SaveState> = {};
  for (const question of attempt.questions) if (question.answer !== null) saves[question.id] = "saved";
  return { kind: "answering", attempt, index, saves, ...(notice ? { notice } : {}) };
}

export interface QuizControls {
  view: QuizView;
  /** The material whose quiz is open, or was last. */
  item: TrainingItem | null;
  /** The track it was opened in. */
  trackId: string | null;
  open: (item: TrainingItem, trackId: string) => void;
  close: () => void;
  choose: (questionId: string, choiceId: string) => void;
  retrySave: (questionId: string) => void;
  next: () => void;
  back: () => void;
  submit: () => void;
  /** Starts again: a new attempt after a fail, or the same request after the unavailable panel. */
  tryAgain: () => void;
}

/**
 * The open quiz: one at a time, short-lived, and kept apart from the training
 * list's own saves. Gatekeeper keeps the attempt, so closing loses nothing.
 */
export function useQuiz({ passed, refresh, retry }: Pick<TrainingControls, "passed" | "refresh" | "retry">): QuizControls {
  const [view, setView] = useState<QuizView>({ kind: "closed" });
  const [item, setItem] = useState<TrainingItem | null>(null);
  const [trackId, setTrackId] = useState<string | null>(null);
  // Saves finish long after the render that started them, as in useTraining.
  const latest = useRef<QuizView>(view);
  const session = useRef<Session | null>(null);
  const queues = useRef(new Map<string, AnswerQueue>());

  const set = useCallback((next: QuizView) => {
    latest.current = next;
    setView(next);
  }, []);

  const gone = useCallback(
    (s: Session) => {
      if (session.current !== s) return;
      session.current = null;
      queues.current.clear();
      set({ kind: "closed", gone: true });
      toast.error(QUIZ_GONE);
      refresh();
    },
    [refresh, set],
  );

  /** The track ended mid-quiz: no call can succeed now, so it closes, says why, and the list re-reads to lock the row. */
  const expired = useCallback(
    (s: Session) => {
      if (session.current !== s) return;
      session.current = null;
      queues.current.clear();
      set({ kind: "closed" });
      toast.error(TRACK_EXPIRED);
      refresh();
    },
    [refresh, set],
  );

  /** Signed out mid-quiz: the training page's own sign-in takes it from here. */
  const signedOut = useCallback(
    (s: Session) => {
      if (session.current !== s) return;
      session.current = null;
      queues.current.clear();
      set({ kind: "closed" });
      retry();
    },
    [retry, set],
  );

  /** The refusals that end the quiz whichever call meets them. True when it has ended. */
  const ended = useCallback(
    (s: Session, failed: Failed): boolean => {
      if (failed.status === 401) signedOut(s);
      else if (failed.code === "unknown_quiz" || failed.code === "unknown_material") gone(s);
      else if (failed.code === "track_expired") expired(s);
      else return false;
      return true;
    },
    [expired, gone, signedOut],
  );

  const start = useCallback(
    async (s: Session) => {
      const started = await startQuiz(fetchNow, s.item.materialId);
      if (session.current !== s) return;
      if (!started.ok) {
        if (!ended(s, started)) set({ kind: "unavailable", retrying: false });
        return;
      }
      const attempt = started.body;
      queues.current.clear();
      trackTraining("training_quiz_started", {
        material_id: s.item.materialId,
        track_id: s.trackId,
        resumed: attempt.answered > 0 || s.resumable,
        answered: attempt.answered,
        total: attempt.total,
      });
      set(answering(attempt));
    },
    [ended, set],
  );

  /** The attempt was replaced or closed elsewhere: read where things stand, once. */
  const reload = useCallback(
    async (s: Session) => {
      if (s.reloaded) {
        set({ kind: "unavailable", retrying: false });
        return;
      }
      s.reloaded = true;
      const state = await loadQuizState(fetchNow, s.item.materialId);
      if (session.current !== s) return;
      if (!state.ok) {
        if (!ended(s, state)) set({ kind: "unavailable", retrying: false });
        return;
      }
      queues.current.clear();
      if (state.body.attempt) set(answering(state.body.attempt));
      else if (state.body.lastResult) {
        // Submitted in another tab: show that result, and let the list catch up.
        set({ kind: "result", result: state.body.lastResult });
        s.dirty = false;
        refresh();
      } else void start(s);
    },
    [ended, refresh, set, start],
  );

  const drain = useCallback(
    async (s: Session, questionId: string) => {
      const queue = queues.current.get(questionId);
      if (!queue) return;
      queue.sending = true;
      let sent: string;
      let saved: Awaited<ReturnType<typeof saveAnswer>>;
      do {
        sent = queue.desired;
        saved = await saveAnswer(fetchNow, s.item.materialId, questionId, sent);
        if (session.current !== s) {
          // Closed while this was out: the row still catches up with it.
          if (saved.ok) refresh();
          return;
        }
        // Picked again while that was out: send the latest choice too.
      } while (saved.ok && queue.desired !== sent);
      queues.current.delete(questionId);

      const current = latest.current;
      if (current.kind !== "answering" && current.kind !== "submitting") return;
      if (saved.ok) {
        s.dirty = true;
        if (saved.body.attemptId !== current.attempt.attemptId) void reload(s);
        else set({ ...current, saves: { ...current.saves, [questionId]: "saved" } });
        return;
      }
      trackTraining("training_quiz_failed_to_save", { material_id: s.item.materialId, status: saved.status });
      if (ended(s, saved)) return;
      if (saved.code === "no_attempt" || saved.code === "invalid_answer") void reload(s);
      // The choice stays on screen; only Next waits for it.
      else set({ ...current, saves: { ...current.saves, [questionId]: "failed" } });
    },
    [ended, refresh, reload, set],
  );

  const open = useCallback(
    (opened: TrainingItem, openedIn: string) => {
      if (latest.current.kind !== "closed" || !canTakeQuiz(opened)) return;
      const s: Session = {
        item: opened,
        trackId: openedIn,
        attempts: opened.quiz?.attempts ?? 0,
        resumable: Boolean(opened.quiz?.inProgress),
        reloaded: false,
        dirty: false,
      };
      session.current = s;
      setItem(opened);
      setTrackId(openedIn);
      set({ kind: "loading" });
      void start(s);
    },
    [set, start],
  );

  const close = useCallback(() => {
    const s = session.current;
    session.current = null;
    queues.current.clear();
    set({ kind: "closed" });
    // The row then says how far the attempt got; unchanged, or already re-read after a pass, it needs nothing.
    if (s?.dirty) refresh();
  }, [refresh, set]);

  const choose = useCallback(
    (questionId: string, choiceId: string) => {
      const current = latest.current;
      const s = session.current;
      if (current.kind !== "answering" || !s) return;
      const attempt = {
        ...current.attempt,
        questions: current.attempt.questions.map((question) =>
          question.id === questionId ? { ...question, answer: choiceId } : question,
        ),
      };
      set({ kind: "answering", attempt, index: current.index, saves: { ...current.saves, [questionId]: "saving" } });
      const queue = queues.current.get(questionId) ?? { sending: false, desired: choiceId };
      queue.desired = choiceId;
      queues.current.set(questionId, queue);
      if (!queue.sending) void drain(s, questionId);
    },
    [drain, set],
  );

  const retrySave = useCallback(
    (questionId: string) => {
      const current = latest.current;
      if (current.kind !== "answering") return;
      const answer = current.attempt.questions.find((question) => question.id === questionId)?.answer;
      if (answer) choose(questionId, answer);
    },
    [choose],
  );

  const move = useCallback(
    (by: 1 | -1) => {
      const current = latest.current;
      if (current.kind !== "answering") return;
      const index = current.index + by;
      if (index < 0 || index >= current.attempt.questions.length) return;
      const question = current.attempt.questions[current.index];
      if (by === 1 && (!question || current.saves[question.id] !== "saved")) return;
      set({ kind: "answering", attempt: current.attempt, index, saves: current.saves });
    },
    [set],
  );
  const next = useCallback(() => move(1), [move]);
  const back = useCallback(() => move(-1), [move]);

  const submit = useCallback(async () => {
    const current = latest.current;
    const s = session.current;
    if (current.kind !== "answering" || !s || !allSaved(current) || queues.current.size > 0) return;
    const { attempt, index, saves } = current;
    set({ kind: "submitting", attempt, index, saves });

    const submitted = await submitQuiz(fetchNow, s.item.materialId);
    // Closed while this was out: the attempt still counts, but nothing reopens.
    const closed = session.current !== s;
    if (submitted.ok) {
      const { result, completedAt } = submitted.body;
      s.attempts += 1;
      s.resumable = false;
      trackTraining("training_quiz_submitted", {
        material_id: s.item.materialId,
        track_id: s.trackId,
        passed: result.passed,
        percent: result.percent,
        correct: result.correct,
        total: result.total,
        attempt: s.attempts,
      });
      if (!closed) set({ kind: "result", result });
      if (result.passed && completedAt) {
        // `passed` re-reads the list, so closing does not have to.
        s.dirty = false;
        for (const track of passed(s.item.materialId, completedAt, result.percent)) celebrate(track);
      } else if (closed) refresh();
      else s.dirty = true;
      return;
    }
    if (closed || ended(s, submitted)) return;
    if (submitted.code === "no_attempt") return void reload(s);
    if (submitted.code === "incomplete") {
      // Somewhere an answer Gatekeeper does not have: go to the first one it is missing.
      const state = await loadQuizState(fetchNow, s.item.materialId);
      if (session.current !== s || (!state.ok && ended(s, state))) return;
      if (state.ok && state.body.attempt) {
        queues.current.clear();
        set(answering(state.body.attempt, "incomplete"));
      } else set({ kind: "unavailable", retrying: false });
      return;
    }
    set({ kind: "answering", attempt, index, saves, notice: "submit_failed" });
  }, [ended, passed, refresh, reload, set]);

  const tryAgain = useCallback(() => {
    const s = session.current;
    if (!s) return;
    s.reloaded = false;
    const current = latest.current;
    if (current.kind === "unavailable") set({ kind: "unavailable", retrying: true });
    else if (current.kind === "result") set({ kind: "loading" });
    else return;
    void start(s);
  }, [set, start]);

  return {
    view,
    item,
    trackId,
    open,
    close,
    choose,
    retrySave,
    next,
    back,
    submit: () => void submit(),
    tryAgain,
  };
}
